/**
 * The rules every text we compose obeys (GH-861). Pure functions; the API applies them.
 *
 *  - **Quiet hours.** No non-verification text between 21:00 and 08:00 in the recipient's local
 *    time (TCPA's 8am–9pm calling window, 47 C.F.R. §64.1200(c)(1), which carriers and the FCC apply
 *    to texts). The zone comes from the area code; an area code spanning zones must be inside the
 *    window in **every** zone it spans; an unknown one is treated as America/New_York and says so
 *    (`inferred: false`). A verification code is exempt: the donor asked for it seconds ago.
 *  - **Keywords.** STOP/START/HELP and their synonyms, matched on the whole message as Twilio
 *    matches them, so our record and Twilio's own block list agree. Nothing else of a message is
 *    kept.
 *  - **The verification text**, one GSM-7 segment, ending with the origin-bound line WebOTP and
 *    iOS read (`@host #code`, the last line — https://wicg.github.io/sms-one-time-codes/ and Apple's
 *    "Enabling AutoFill for domain-bound SMS codes").
 *  - **The split.** Which code provider a number gets when both are compared.
 */
/* ------------------------------------------------------------------ quiet hours */
export const SMS_QUIET_HOURS = { startHour: 21, endHour: 8 };
const ET = "America/New_York";
const CT = "America/Chicago";
const MT = "America/Denver";
const PT = "America/Los_Angeles";
/**
 * NANP area codes by time zone, 2026. Where an area code spans zones it lists each, earliest zone
 * first, and the window must hold in all of them. Overlays share their parent's zones.
 *
 * Deliberately a table rather than a phone-number library: the question is narrow (which local
 * clock does this handset most likely read) and the answer must be reviewable in one place.
 */
const ZONES_BY_AREA_CODE = (() => {
    const table = new Map();
    const add = (zones, codes) => {
        for (const code of codes.trim().split(/\s+/))
            table.set(code, zones);
    };
    // ---- United States
    // Eastern
    add([ET], `
    202 771 203 475 860 959 302 239 305 321 324 352 386 407 561 645 656 689 727 728 754 772 786
    813 863 904 941 954 229 404 470 478 678 706 762 770 912 943 260 317 463 574 765 502 859 606
    207 227 240 301 410 443 667 339 351 413 508 617 774 781 857 978 231 248 269 313 517 586 616
    679 734 810 947 989 603 201 551 609 640 732 848 856 862 908 973 212 315 332 347 363 516 518
    585 607 624 631 646 680 716 718 838 845 914 917 929 934 252 336 472 704 743 828 910 919 980
    984 216 220 234 283 326 330 380 419 436 440 513 567 614 740 937 215 223 267 272 412 445 484
    570 582 610 717 724 814 835 878 401 803 839 843 854 864 865 423 802 276 434 540 571 686 703
    757 804 826 948 304 681
  `);
    // Central
    add([CT], `
    205 251 256 334 659 938 327 479 501 870 217 224 309 312 331 447 464 618 630 708 730 773 779
    815 847 861 872 219 319 515 563 641 712 316 913 225 318 337 504 985 218 320 507 612 651 763
    952 228 601 662 769 314 417 557 573 636 660 816 975 402 531 405 539 572 580 918 615 629 731
    901 931 210 214 254 281 325 346 361 409 430 432 469 512 682 713 726 737 806 817 830 832 903
    936 940 945 956 972 979 262 274 414 534 608 715 920
  `);
    // Mountain (Denver observes daylight time; Arizona does not)
    add([MT], "303 719 720 970 983 406 505 575 385 435 801 307 915");
    add(["America/Phoenix"], "480 520 602 623 928");
    // Pacific
    add([PT], `
    209 213 279 310 323 341 350 369 408 415 424 442 510 530 559 562 619 626 628 650 657 661 669
    707 714 747 760 805 818 820 831 840 858 909 916 925 949 951 702 725 775 458 503 971 206 253
    360 425 509 564
  `);
    // Spanning two zones
    add([ET, CT], "850 448 812 930 270 364 906");
    add([CT, MT], "620 785 308 701 605");
    add([MT, PT], "208 986 541");
    // Non-contiguous states and territories
    add(["America/Anchorage"], "907");
    add(["Pacific/Honolulu"], "808");
    add(["America/Puerto_Rico"], "787 939");
    add(["America/St_Thomas"], "340");
    add(["Pacific/Guam"], "671");
    add(["Pacific/Saipan"], "670");
    add(["Pacific/Pago_Pago"], "684");
    // ---- Canada
    add(["America/Toronto"], `
    226 249 289 343 365 382 416 437 519 548 613 647 683 705 742 753 905 942 263 354 367 418 438
    450 468 514 579 581 819 873
  `);
    add(["America/Toronto", "America/Winnipeg"], "807");
    add(["America/Winnipeg"], "204 431 584");
    add(["America/Regina"], "306 474 639");
    add(["America/Edmonton"], "368 403 587 780 825");
    add(["America/Edmonton", "America/Vancouver"], "250");
    add(["America/Vancouver"], "236 257 604 672 778");
    add(["America/Halifax"], "428 506 782 902");
    add(["America/St_Johns"], "709 879");
    add(["America/Toronto", "America/Winnipeg", "America/Edmonton", "America/Whitehorse"], "867");
    return table;
})();
export function timeZonesForNumber(e164) {
    const areaCode = /^\+1(\d{3})\d{7}$/.exec(e164)?.[1];
    const zones = areaCode ? ZONES_BY_AREA_CODE.get(areaCode) : undefined;
    return zones ? { zones, inferred: true } : { zones: [ET], inferred: false };
}
function localHour(at, zone) {
    const hour = new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        hour: "numeric",
        hourCycle: "h23",
    }).format(at);
    return Number(hour);
}
const withinWindow = (at, zones) => zones.every((zone) => {
    const hour = localHour(at, zone);
    return hour >= SMS_QUIET_HOURS.endHour && hour < SMS_QUIET_HOURS.startHour;
});
const QUARTER_HOUR_MS = 15 * 60 * 1000;
const SEARCH_LIMIT_MS = 48 * 60 * 60 * 1000;
export function smsQuietHours(e164, now) {
    const { zones, inferred } = timeZonesForNumber(e164);
    if (withinWindow(now, zones))
        return { allowed: true, zones, inferred, notBefore: null };
    const start = Math.ceil(now.getTime() / QUARTER_HOUR_MS) * QUARTER_HOUR_MS;
    for (let at = start; at - now.getTime() <= SEARCH_LIMIT_MS; at += QUARTER_HOUR_MS) {
        if (withinWindow(new Date(at), zones)) {
            return { allowed: false, zones, inferred, notBefore: new Date(at) };
        }
    }
    return { allowed: false, zones, inferred, notBefore: null };
}
/* ------------------------------------------------------------------ keywords */
/** Twilio's standard opt-out set (https://help.twilio.com/articles/223134027). */
const OPT_OUT = new Set([
    "STOP",
    "STOPALL",
    "UNSUBSCRIBE",
    "CANCEL",
    "END",
    "QUIT",
    "REVOKE",
    "OPTOUT",
]);
const OPT_IN = new Set(["START", "UNSTOP", "YES"]);
const HELP = new Set(["HELP", "INFO"]);
/**
 * `optOutType` is Twilio's `OptOutType` (STOP / START / HELP), present when Advanced Opt-Out is on.
 * It is believed over our own list because it reflects keywords the owner configured in Twilio
 * (another language, say), and Twilio has already acted on it.
 */
export function parseSmsKeyword(body, optOutType) {
    switch (optOutType?.toUpperCase()) {
        case "STOP":
            return { action: "opt_out", keyword: "STOP" };
        case "START":
            return { action: "opt_in", keyword: "START" };
        case "HELP":
            return { action: "help", keyword: "HELP" };
        default:
            break;
    }
    const word = body.trim().toUpperCase();
    if (OPT_OUT.has(word))
        return { action: "opt_out", keyword: word };
    if (OPT_IN.has(word))
        return { action: "opt_in", keyword: word };
    if (HELP.has(word))
        return { action: "help", keyword: word };
    return { action: "none", keyword: null };
}
/* ------------------------------------------------------------------ copy */
/**
 * The HELP reply: who we are, where help is, how to stop, and the carrier disclosure (CTIA's
 * Messaging Principles ask for all four). One GSM-7 segment. Also the text to paste into Twilio's
 * Advanced Opt-Out HELP reply, so the two never differ.
 */
export function smsHelpText(supportContact) {
    return (`GiveHere: texts about your gifts and codes you request. Help: ${supportContact}. ` +
        "Reply STOP to stop texts. Msg & data rates may apply.");
}
/** With the deployed default support address (`infra/deploy.sh`'s SUPPORT_CONTACT). */
export const SMS_HELP_TEXT = smsHelpText("support@bedierfoundation.org");
/** ADDENDUM item 31's wording. */
export const SMS_STOP_LANGUAGE = "Reply STOP to stop texts.";
export function appendStopLanguage(body) {
    return body.includes(SMS_STOP_LANGUAGE) ? body : `${body.trimEnd()} ${SMS_STOP_LANGUAGE}`;
}
const HOST = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
/**
 * "GiveHere code: 123456. Don't share it." and, where the deployment knows the web origin a donor
 * types it into, a blank line and `@host #123456` — the last line, nothing before the `@`, exactly
 * one space, as the WebOTP draft and Apple require. Brand-neutral: no organization, no gift.
 */
export function verificationText(code, originHost) {
    if (!/^\d{4,10}$/.test(code))
        throw new Error("A verification code is digits only.");
    const first = `GiveHere code: ${code}. Don't share it.`;
    if (originHost === null)
        return first;
    if (!HOST.test(originHost))
        throw new Error("SMS_OTP_ORIGIN_HOST must be a bare host name.");
    return `${first}\n\n@${originHost} #${code}`;
}
/* ------------------------------------------------------------------ segments */
const GSM7_BASIC = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXTENSION = "^{}\\[~]|€\f";
export function isGsm7(text) {
    for (const character of text) {
        if (!GSM7_BASIC.includes(character) && !GSM7_EXTENSION.includes(character))
            return false;
    }
    return true;
}
/** How many SMS segments a carrier bills: 160/153 septets in GSM-7, 70/67 UTF-16 units otherwise. */
export function smsSegments(text) {
    if (isGsm7(text)) {
        let septets = 0;
        for (const character of text)
            septets += GSM7_EXTENSION.includes(character) ? 2 : 1;
        return septets <= 160 ? 1 : Math.ceil(septets / 153);
    }
    const units = text.length;
    return units <= 70 ? 1 : Math.ceil(units / 67);
}
/* ------------------------------------------------------------------ limits */
/**
 * Per recipient, for messages (never codes, which have their own per-number throttles): at most
 * three a day, and never two within a minute. A donor who gives twice at one event gets two
 * receipts; a loop or a script never gets a fourth.
 */
export const SMS_MESSAGES_PER_RECIPIENT_PER_DAY = 3;
export const SMS_MESSAGE_MIN_INTERVAL_MS = 60 * 1000;
/**
 * Twilio's errors for "our Messaging Service has no sender that can reach this number" — today,
 * every U.S. and Canadian number, because the service holds only an alphanumeric sender. While our
 * sender is failing this way (`OWN_CODE_FALLBACK_WINDOW_MS`), own-code assignments go to Twilio
 * Verify instead, so a donor's resend reaches them.
 *
 * Two kinds, because a caller chooses the number (security review, GH-861):
 *  - **service-wide** (21703, 21704): the service has no usable sender at all. One is enough.
 *  - **per destination** (21612, 21606): reachability depends on the number a caller typed, so one
 *    such failure could be induced on purpose to push everyone to Verify. It takes failures for
 *    `OWN_CODE_FALLBACK_DISTINCT_NUMBERS` different numbers in the window.
 */
export const OWN_CODE_SERVICE_WIDE_ERROR_CODES = ["21703", "21704"];
export const OWN_CODE_DESTINATION_ERROR_CODES = ["21612", "21606"];
export const OWN_CODE_NO_SENDER_ERROR_CODES = [
    ...OWN_CODE_SERVICE_WIDE_ERROR_CODES,
    ...OWN_CODE_DESTINATION_ERROR_CODES,
];
export const OWN_CODE_FALLBACK_DISTINCT_NUMBERS = 3;
export const OWN_CODE_FALLBACK_WINDOW_MS = 15 * 60 * 1000;
/**
 * A number's bucket, 0..99, from its keyed fingerprint (a SHA-256 hex digest). Stable across
 * requests, instances and restarts, and meaningless without the contact key.
 */
export function splitBucket(fingerprint) {
    const head = Number.parseInt(fingerprint.slice(0, 8), 16);
    return Number.isFinite(head) ? head % 100 : 0;
}
/**
 * By number, not by request: the same donor always gets the same provider, so a resend goes the
 * same way and a comparison is never one person's two experiences. Buckets below the percentage
 * get our own codes, so raising it only ever moves numbers toward own codes.
 */
export function assignCodeProvider(fingerprint, ownCodePercent) {
    return splitBucket(fingerprint) < ownCodePercent ? "own_code" : "twilio_verify";
}
//# sourceMappingURL=sms.js.map